import { useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email });
      // Always show the success screen regardless — we don't leak whether the
      // email exists in our system.
      setSent(true);
    } catch (err) {
      // We still claim success (defense against enumeration) but log for ops
      console.error(err);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6 md:p-12">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-6"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-electric flex items-center justify-center">
            <Shield size={20} className="text-black" />
          </div>
          <div className="label-mono">VERIFIED · Reset Password</div>
        </div>

        {!sent ? (
          <>
            <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
              Forgot your <span className="text-electric">password?</span>
            </h1>
            <p className="text-white/50 text-sm mt-3">
              Enter the email on your account and we'll send you a secure reset link.
              The link expires in 30 minutes.
            </p>
            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="label-mono block mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark w-full rounded-md px-4 py-3 text-sm"
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                <Mail size={14} /> {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <div className="space-y-5" data-testid="forgot-success">
            <div className="w-12 h-12 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
              <CheckCircle2 size={22} className="text-emerald-300" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
              Check your <span className="text-electric">inbox</span>.
            </h1>
            <p className="text-white/60 text-sm leading-relaxed">
              If an account exists for <span className="text-white font-mono">{email}</span>,
              you'll receive a password reset link within the next minute.
              The link is valid for 30 minutes.
            </p>
            <p className="text-white/40 text-xs">
              Didn't get it? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-electric hover:underline"
                data-testid="forgot-try-again"
              >
                try a different email
              </button>
              .
            </p>
            <Link to="/login" className="btn-ghost w-full justify-center" data-testid="forgot-back-btn">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
